import clsx from 'clsx';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';

import { Icon } from '../icon';
import { Modal } from '../modal';
import { type ButtonProps } from '../button';
import { AlertMessage } from './parts/alert-message';
import { dismissAllPopovers, Popover, type PopoverProps } from '../popover';

import styles from './alert.module.scss';

export const Alert = (props: AlertProps) => {
  const {
    icon,
    type,
    title,
    color,
    onOpen,
    trigger,
    message,
    onClose,
    maxWidth,
    withOverlay,
    openInModal,
    popoverProps,
    primaryButton,
    secondaryButton,
    open: alertOpen,
  } = props;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!!alertOpen);
  }, [alertOpen]);

  const iconType = type === 'danger' ? 'delete-01-bulk' : icon;

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const handleOpen = () => {
    setOpen(true);
    onOpen?.();
  };

  const sharedProps = {
    title,
    onClose: handleClose,
    secondaryButton: {
      appearance: 'clear',
      children: type === 'danger' ? 'Keep' : 'Cancel',
      ...secondaryButton,
      onClick: () => {
        secondaryButton?.onClick ? secondaryButton?.onClick?.(handleClose) : handleClose();
      },
    } as ButtonProps,
    primaryButton: {
      children: type === 'danger' ? 'Delete' : 'Close',
      color: [type, color].includes('danger') ? 'danger' : color ?? 'dark',
      ...primaryButton,
      onClick: () => {
        primaryButton?.onClick ? primaryButton?.onClick?.(handleClose) : handleClose;
        dismissAllPopovers();
      },
    } as ButtonProps,
  };

  if (openInModal) {
    return (
      <Modal
        open={open}
        onOpen={handleOpen}
        data-testid="alert-modal"
        maxWidth={maxWidth || 350}
        modalContentHolderClassName={styles.alertContentHolder}
        className={clsx(styles.alertMessageContent, styles[`t-${type}`], styles[`c-${color}`])}
        {...sharedProps}
        closeOnEscape={false}
      >
        {iconType && (
          <div className={styles.icon}>
            <Icon multiColor type={iconType} size={30} />
          </div>
        )}
        <div>{message}</div>
      </Modal>
    );
  }

  return (
    <Popover
      arrow
      open={open}
      offset={[0, 10]}
      onOpen={handleOpen}
      onClose={handleClose}
      cornerRadius="rounded"
      withOverlay={withOverlay}
      maxWidth={maxWidth || 290}
      onContentClick={(e) => e.stopPropagation()}
      content={
        <AlertMessage data-testid="alert-popover" color={color} type={type} icon={iconType} message={message} {...sharedProps} />
      }
      {...popoverProps}
    >
      {trigger}
    </Popover>
  );
};

export interface AlertProps {
  icon?: string;
  open?: boolean;
  message: string;
  title: ReactNode;
  maxWidth?: number;
  onOpen?: () => void;
  onClose?: () => void;
  openInModal?: boolean;
  trigger?: ReactElement;
  withOverlay?: boolean;
  primaryButton?: Omit<ButtonProps, 'onClick'> & {
    onClick?: (cb?: () => void) => void;
  };
  secondaryButton?: Omit<ButtonProps, 'onClick'> & {
    onClick?: (cb?: () => void) => void;
  };
  popoverProps?: Partial<PopoverProps>;
  type?: 'danger' | 'warning' | 'info';
  color?: 'danger' | 'hero';
}
